import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Loader2, Building2, Save, ChevronLeft, ChevronRight, Camera, Calendar,
  LayoutGrid, ImageIcon, ListChecks, LogOut, Check, X, Sparkles,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getPartnerAccess, savePartnerSignup, listApprovedCategories } from "@/lib/partner.functions";
import { MAX_ONBOARDING_PHOTOS, MAX_PHOTOS_MESSAGE } from "@/lib/onboarding-limits";
import { usePublicCities } from "@/hooks/use-public-cities";

export const Route = createFileRoute("/_authenticated/partner/onboarding")({
  head: () => ({ meta: [{ title: "Partner Onboarding — What Finland" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: PartnerOnboarding,
});

type StepId = "basic" | "categories" | "media" | "events" | "review";
type Segment = "eat_drink" | "do" | "happenings";

const STEPS: { id: StepId; label: string; Icon: typeof Building2 }[] = [
  { id: "basic", label: "Basic Info", Icon: Building2 },
  { id: "categories", label: "Place Categories", Icon: LayoutGrid },
  { id: "media", label: "Media", Icon: ImageIcon },
  { id: "events", label: "Event Sync", Icon: Calendar },
  { id: "review", label: "Review & Publish", Icon: ListChecks },
];

const SEGMENT_OPTIONS: { id: Segment; label: string; section: "eat" | "do" | "happening" }[] = [
  { id: "eat_drink", label: "Eat & Drink", section: "eat" },
  { id: "do", label: "Things To Do", section: "do" },
  { id: "happenings", label: "Happenings", section: "happening" },
];

const VIBES = [
  { id: "nature", label: "Nature Escape", emoji: "🌲" },
  { id: "relax", label: "Relax & Wellness", emoji: "🧖" },
  { id: "outdoor", label: "Outdoor Adventures", emoji: "🏞️" },
  { id: "food", label: "Food & Local Tastes", emoji: "🍽️" },
  { id: "nightlife", label: "Nightlife & Culture", emoji: "🎭" },
];

function PartnerOnboarding() {
  const navigate = useNavigate();
  const accessFn = useServerFn(getPartnerAccess);
  const saveFn = useServerFn(savePartnerSignup);
  const catsFn = useServerFn(listApprovedCategories);

  const { data: access, isLoading, refetch } = useQuery({ queryKey: ["partner", "access"], queryFn: () => accessFn() });
  const { data: dbCategories = [] } = useQuery({ queryKey: ["categories", "approved"], queryFn: () => catsFn() });

  const [step, setStep] = useState<StepId>("basic");
  const [form, setForm] = useState({
    business_name: "", contact_name: "", phone: "", city: "",
    website: "", instagram: "", description: "",
  });
  const [address, setAddress] = useState("");
  const [segment, setSegment] = useState<Segment>("eat_drink");
  const [categories, setCategories] = useState<string[]>([]);
  const [proposed, setProposed] = useState<string[]>([]);
  const [newCat, setNewCat] = useState("");
  const [logo, setLogo] = useState<string | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  const [vibes, setVibes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const logoRef = useRef<HTMLInputElement | null>(null);

  const categoryOptions = useMemo(
    () => dbCategories.filter((c) => {
      const sec = SEGMENT_OPTIONS.find((s) => s.id === segment)?.section;
      return !sec || c.section === sec;
    }).map((c) => c.label),
    [dbCategories, segment],
  );

  useEffect(() => {
    if (access?.signup) {
      const s = access.signup as typeof access.signup & {
        logo_url?: string | null;
        photos?: string[] | null;
        categories?: string[] | null;
        vibes?: string[] | null;
        segment?: Segment | null;
        proposed_categories?: string[] | null;
      };
      setForm({
        business_name: s.business_name ?? "",
        contact_name: s.contact_name ?? "",
        phone: s.phone ?? "",
        city: s.city ?? "",
        website: s.website ?? "",
        instagram: s.instagram ?? "",
        description: s.description ?? "",
      });
      if (s.logo_url) setLogo(s.logo_url);
      if (Array.isArray(s.photos)) setPhotos(s.photos);
      if (Array.isArray(s.categories)) setCategories(s.categories);
      if (Array.isArray(s.vibes)) setVibes(s.vibes);
      if (Array.isArray(s.proposed_categories)) setProposed(s.proposed_categories);
      if (s.segment) setSegment(s.segment);
    }
  }, [access?.signup]);

  useEffect(() => {
    if (!access) return;
    if (access.hasClient) navigate({ to: "/partner" as never });
    else if (access.signup?.status === "submitted") navigate({ to: "/partner/pending" as never });
  }, [access, navigate]);

  const stepIdx = STEPS.findIndex((s) => s.id === step);
  const infoDone = useMemo(() => !!(form.business_name.trim() && form.contact_name.trim() && form.city.trim()), [form]);
  const isLast = step === "review";

  const payload = () => ({
    ...form,
    logo_url: logo,
    photos,
    categories,
    vibes,
    segment,
    proposed_categories: proposed,
  });

  async function saveDraft() {
    setBusy(true);
    const res = await saveFn({ data: { ...payload(), submit: false } });
    setBusy(false);
    if (!res.ok) return toast.error(res.error);
    toast.success("Draft saved");
    refetch();
  }

  async function submitForReview() {
    if (!infoDone) { setStep("basic"); return toast.error("Please complete Basic Info first."); }
    setBusy(true);
    const res = await saveFn({ data: { ...payload(), submit: true } });
    setBusy(false);
    if (!res.ok) return toast.error(res.error === "missing_required" ? "Fill business name, your name and city." : res.error);
    toast.success("Application submitted");
    navigate({ to: "/partner/set-password" as never });
  }

  function validateStep(id: StepId): string | null {
    if (id === "basic") {
      if (!form.business_name.trim()) return "Enter your place name.";
      if (!form.contact_name.trim()) return "Enter your name.";
      if (!form.city.trim()) return "Enter your city.";
      if (!address.trim()) return "Enter your address.";
      if (!form.description.trim()) return "Add a short description.";
      if (categories.length === 0 && proposed.length === 0) return "Pick at least one category (or propose a new one).";
    }
    if (id === "categories" && categories.length === 0 && proposed.length === 0) return "Pick at least one category.";
    if (id === "media" && photos.length === 0) return "Upload at least one photo.";
    return null;
  }

  function nextStep() {
    if (isLast) return submitForReview();
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    setStep(STEPS[Math.min(stepIdx + 1, STEPS.length - 1)].id);
  }
  function prevStep() { setStep(STEPS[Math.max(stepIdx - 1, 0)].id); }

  function goToStep(target: StepId) {
    const targetIdx = STEPS.findIndex((s) => s.id === target);
    if (targetIdx <= stepIdx) { setStep(target); return; }
    // Forward jumps: validate every step in between
    for (let i = 0; i < targetIdx; i++) {
      const err = validateStep(STEPS[i].id);
      if (err) { setStep(STEPS[i].id); toast.error(err); return; }
    }
    setStep(target);
  }


  function onPhotos(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const remaining = Math.max(0, MAX_ONBOARDING_PHOTOS - photos.length);
    const wouldExceed = files.length > remaining;
    const accepted = files.slice(0, remaining);
    const readers = accepted.map(
      (f) => new Promise<string>((res) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(f); }),
    );
    Promise.all(readers).then((urls) => setPhotos((p) => [...p, ...urls].slice(0, MAX_ONBOARDING_PHOTOS)));
    if (wouldExceed) toast.error(MAX_PHOTOS_MESSAGE);
    // Reset the input so re-selecting the same files re-triggers change.
    if (fileRef.current) fileRef.current.value = "";
  }

  function onLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setLogo(String(r.result));
    r.readAsDataURL(f);
  }

  function addProposed() {
    const label = newCat.trim();
    if (!label) return;
    if (categoryOptions.some((c) => c.toLowerCase() === label.toLowerCase())) {
      toast.info("That category already exists — pick it above.");
      setNewCat("");
      return;
    }
    if (proposed.some((c) => c.toLowerCase() === label.toLowerCase())) return;
    setProposed((p) => [...p, label]);
    setNewCat("");
    toast.success("Proposed — needs admin approval before it appears on your listing.");
  }

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/partner/login" as never, replace: true });
  }

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  const nextLabel = isLast ? "Submit For Review" : `Next Step: ${STEPS[stepIdx + 1]?.label ?? "Continue"}`;

  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      {/* Brand header */}
      <header className="pt-6 sm:pt-8 pb-3 sm:pb-4 text-center px-4">
        <div className="text-2xl sm:text-3xl font-serif tracking-tight text-[#0B1220]" style={{ fontFamily: "Fraunces, ui-serif, serif", fontWeight: 700 }}>what</div>
        <div className="mt-1 text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] text-[#0B1220]/80 font-semibold uppercase">Partner Onboarding</div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 pb-24 lg:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 lg:gap-6">
          {/* Mobile stepper — horizontal scroll */}
          <nav className="lg:hidden -mx-3 px-3 overflow-x-auto no-scrollbar">
            <ol className="flex items-center gap-2 min-w-max pb-1">
              {STEPS.map((s, i) => {
                const active = s.id === step;
                const done = i < stepIdx || (s.id === "basic" && infoDone && !active);
                return (
                  <li key={s.id} className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => goToStep(s.id)}
                      className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        active ? "bg-[#0B1220] text-white" : done ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-white border border-slate-200 text-slate-600"
                      }`}
                    >
                      <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] ${
                        active ? "bg-white text-[#0B1220]" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        {done && !active ? <Check className="h-3 w-3" /> : i + 1}
                      </span>
                      <span className="whitespace-nowrap">{s.label}</span>
                    </button>
                    {i < STEPS.length - 1 && <span className="h-px w-3 bg-slate-300 shrink-0" aria-hidden />}
                  </li>
                );
              })}
            </ol>
          </nav>

          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block bg-white rounded-2xl border border-slate-200 p-4 h-fit lg:sticky lg:top-6">
            <div className="text-xs font-semibold text-slate-600 px-2 pb-3">Your Progress</div>
            <ol className="relative space-y-1">
              {STEPS.map((s, i) => {
                const active = s.id === step;
                const done = i < stepIdx || (s.id === "basic" && infoDone && !active);
                return (
                  <li key={s.id} className="relative">
                    {i < STEPS.length - 1 && <span className="absolute left-[22px] top-9 bottom-[-4px] w-px bg-slate-200" aria-hidden />}
                    <button
                      onClick={() => goToStep(s.id)}
                      className={`w-full flex items-center gap-3 rounded-xl px-2 py-2 text-sm transition ${
                        active ? "bg-[#0B1220] text-white" : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                        active ? "bg-white text-[#0B1220]" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        {done && !active ? <Check className="h-4 w-4" /> : <s.Icon className="h-4 w-4" />}
                      </span>
                      <span className="font-medium">{s.label}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <button onClick={signOut} className="mt-4 w-full text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 px-2">
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </aside>

          {/* Main */}
          <main>
            <div className="mb-4 sm:mb-5">
              <h1 className="text-xl sm:text-3xl font-semibold text-[#0B1220]" style={{ fontFamily: "Fraunces, ui-serif, serif" }}>
                {step === "basic" && "Let's List Your Place!"}
                {step === "categories" && "Pick Your Categories"}
                {step === "media" && "Showcase Your Place"}
                {step === "events" && "Sync Your Events"}
                {step === "review" && "Review & Publish"}
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-slate-600">
                {step === "basic" && "Tell us about your unique spot to help locals and visitors discover you."}
                {step === "categories" && "Choose one or more categories that describe your place."}
                {step === "media" && `Upload up to ${MAX_ONBOARDING_PHOTOS} photos that best represent your place.`}
                {step === "events" && "Connect your event sources so we can keep your listing fresh."}
                {step === "review" && "One last look before we send it to our editors."}
              </p>
            </div>

            {/* Panels */}
            {step === "basic" && (
              <>
                <Card num={1} title="Place Details">
                  <Field label="Place Name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} placeholder="Löyly Helsinki" />
                  <div>
                    <Label>Segment</Label>
                    <div className="flex flex-wrap gap-2">
                      {SEGMENT_OPTIONS.map((s) => (
                        <button key={s.id} type="button" onClick={() => { setSegment(s.id); setCategories([]); }}
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium ${segment === s.id ? "bg-[#0B1220] text-white border-[#0B1220]" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">Determines which page your listing appears on after approval (/eat, /do or /happening).</p>
                  </div>
                  <div>
                    <Label>Logo</Label>
                    <div className="flex items-center gap-3">
                      {logo ? <img src={logo} alt="" className="h-14 w-14 rounded-lg object-cover border border-slate-200" /> : <div className="h-14 w-14 rounded-lg border border-dashed border-slate-300 bg-slate-50" />}
                      <button type="button" onClick={() => logoRef.current?.click()} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                        {logo ? "Replace Logo" : "Upload Logo"}
                      </button>
                      {logo && <button type="button" onClick={() => setLogo(null)} className="text-xs text-slate-400 hover:text-rose-600">Remove</button>}
                      <input ref={logoRef} type="file" accept="image/*" hidden onChange={onLogo} />
                    </div>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <select
                      value={categories[0] ?? ""}
                      onChange={(e) => setCategories(e.target.value ? [e.target.value] : [])}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    >
                      <option value="">Sauna, Restaurant, Café, Event Venue, Activity</option>
                      {categoryOptions.map((c: string) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <Field label="Address" value={address} onChange={setAddress} placeholder="Google Maps auto-complete" />
                  <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="Website" />
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Description</Label>
                      <span className="text-xs text-slate-400">{form.description.length} characters</span>
                    </div>
                    <textarea
                      rows={3}
                      value={form.description}
                      maxLength={4000}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                      placeholder="A modern oasis for design, dining, and steaming saunas by the sea."
                    />
                    <div className="text-right text-xs text-slate-400 mt-1">4000</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <Field label="Your Name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
                    <CityPickerField value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                    <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                    <Field label="Instagram" value={form.instagram} onChange={(v) => setForm({ ...form, instagram: v })} placeholder="@handle" />
                  </div>
                </Card>

                <Card num={2} title="Showcase Your Place">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-full rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 hover:bg-slate-50 py-10 flex flex-col items-center justify-center text-slate-500"
                  >
                    <div className="text-xs mb-2">Drag & Drop</div>
                    <Camera className="h-6 w-6 text-slate-600" />
                    <div className="mt-2 text-sm font-medium text-slate-800">Click To Upload Or Drag Photos</div>
                    <div className="text-xs mt-0.5">(maximum {MAX_ONBOARDING_PHOTOS} photos · {photos.length}/{MAX_ONBOARDING_PHOTOS})</div>
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPhotos} />
                  {photos.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {photos.map((src, i) => (
                        <li key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-2">
                          <img src={src} alt="" className="h-10 w-14 rounded-md object-cover" />
                          <div className="flex-1 text-xs text-slate-600">Uploaded Image…</div>
                          <div className="flex-[2] h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-[#0B1220]" style={{ width: "70%" }} /></div>
                          <button onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))} className="text-slate-400 hover:text-slate-900"><X className="h-4 w-4" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>

                <Card num={3} title="Link Your Existing Channels (Optional)" subtitle="Crucial for automation">
                  <div className="text-sm text-slate-700 mb-2">Already have events? Sync them automatically!</div>
                  <ChannelRow name="Google Calendar" color="bg-blue-100 text-blue-700" letter="G" />
                  <ChannelRow name="Eventbrite" color="bg-orange-100 text-orange-700" letter="E" />
                  <div className="pt-2">
                    <Label>City Events API</Label>
                    <div className="flex items-center gap-2">
                      <select className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
                        <option>Helsinki Linked Events</option>
                        <option>Tampere Events</option>
                      </select>
                      <button type="button" className="rounded-xl bg-[#0B1220] text-white text-sm px-4 py-2.5 font-medium hover:bg-[#0B1220]/90">Sync Now</button>
                    </div>
                  </div>
                </Card>

                <Card num={4} title="Vibe Selection" subtitle="Select the vibes that fit">
                  <div className="grid grid-cols-2 gap-2">
                    {VIBES.map((v) => {
                      const on = vibes.includes(v.id);
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setVibes((prev) => on ? prev.filter((x) => x !== v.id) : [...prev, v.id])}
                          className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                            on ? "border-[#0B1220] bg-[#0B1220] text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <span className={`h-4 w-4 rounded border ${on ? "bg-white border-white" : "border-slate-300"} flex items-center justify-center`}>
                            {on && <Check className="h-3 w-3 text-[#0B1220]" />}
                          </span>
                          <span>{v.emoji}</span>
                          <span>{v.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </Card>
              </>
            )}

            {step === "categories" && (
              <Card num={1} title="Pick All That Apply" subtitle={`Approved categories for ${SEGMENT_OPTIONS.find((s) => s.id === segment)?.label}`}>
                {categoryOptions.length === 0 ? (
                  <p className="text-sm text-slate-500">No approved categories yet for this segment. Propose one below.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {categoryOptions.map((c: string) => {
                      const on = categories.includes(c);
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCategories((prev) => on ? prev.filter((x) => x !== c) : [...prev, c])}
                          className={`rounded-xl border px-3 py-2.5 text-sm text-left transition ${
                            on ? "border-[#0B1220] bg-[#0B1220] text-white" : "border-slate-200 bg-white hover:bg-slate-50"
                          }`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="mt-5 pt-4 border-t border-slate-100">
                  <Label>Propose A New Category</Label>
                  <div className="flex gap-2">
                    <input
                      value={newCat}
                      onChange={(e) => setNewCat(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProposed(); } }}
                      placeholder="e.g. Rooftop Bar"
                      className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    />
                    <button type="button" onClick={addProposed} className="rounded-xl bg-white border border-slate-200 px-3 py-2 text-sm font-medium hover:bg-slate-50">Propose</button>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">New categories are hidden from your listing until an admin approves them.</p>
                  {proposed.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {proposed.map((p) => (
                        <span key={p} className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs px-2 py-0.5">
                          {p} <span className="text-amber-600">· pending</span>
                          <button onClick={() => setProposed((arr) => arr.filter((x) => x !== p))} className="ml-0.5 text-amber-600 hover:text-amber-900"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            )}

            {step === "media" && (
              <Card num={2} title="Showcase Your Place">
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="w-full rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 hover:bg-slate-50 py-14 flex flex-col items-center justify-center text-slate-500">
                  <Camera className="h-7 w-7 text-slate-600" />
                  <div className="mt-2 text-sm font-medium text-slate-800">Click To Upload Or Drag Photos</div>
                  <div className="text-xs mt-0.5">(maximum {MAX_ONBOARDING_PHOTOS} photos · {photos.length}/{MAX_ONBOARDING_PHOTOS})</div>
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPhotos} />
                {photos.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos.map((src, i) => (
                      <div key={i} className="relative rounded-xl overflow-hidden bg-slate-100 aspect-video">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-1"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {step === "events" && (
              <Card num={3} title="Link Your Existing Channels" subtitle="Optional but crucial for automation">
                <ChannelRow name="Google Calendar" color="bg-blue-100 text-blue-700" letter="G" />
                <ChannelRow name="Eventbrite" color="bg-orange-100 text-orange-700" letter="E" />
                <div className="pt-2">
                  <Label>City Events API</Label>
                  <div className="flex items-center gap-2">
                    <select className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
                      <option>Helsinki Linked Events</option>
                      <option>Tampere Events</option>
                    </select>
                    <button type="button" className="rounded-xl bg-[#0B1220] text-white text-sm px-4 py-2.5 font-medium hover:bg-[#0B1220]/90">Sync Now</button>
                  </div>
                </div>
              </Card>
            )}

            {step === "review" && (
              <Card num={5} title="Review & Publish" subtitle="Confirm your details">
                <ReviewRow label="Place Name" value={form.business_name || "—"} />
                <ReviewRow label="Contact" value={`${form.contact_name || "—"} · ${form.phone || "no phone"}`} />
                <ReviewRow label="City" value={form.city || "—"} />
                <ReviewRow label="Address" value={address || "—"} />
                <ReviewRow label="Website" value={form.website || "—"} />
                <ReviewRow label="Instagram" value={form.instagram || "—"} />
                <ReviewRow label="Categories" value={categories.join(", ") || "—"} />
                <ReviewRow label="Photos" value={`${photos.length} uploaded`} />
                <ReviewRow label="Vibes" value={vibes.length ? `${vibes.length} selected` : "—"} />
                {!infoDone && (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" /> Complete Basic Info before submitting.
                  </div>
                )}
              </Card>
            )}

            {/* Bottom actions — sticky bar on mobile */}
            <div className="mt-6 fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-3 py-3 flex items-center justify-between gap-2 lg:static lg:border-0 lg:bg-transparent lg:backdrop-blur-0 lg:px-0 lg:py-0">
              <button onClick={prevStep} disabled={stepIdx === 0}
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-40 shrink-0 px-2 py-2">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <button onClick={saveDraft} disabled={busy}
                  className="rounded-full border border-slate-200 bg-white px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 shrink-0">
                  <Save className="h-4 w-4" /> <span className="hidden xs:inline sm:inline">Save Draft</span><span className="xs:hidden sm:hidden">Save</span>
                </button>
                <button onClick={nextStep} disabled={busy}
                  className="rounded-full bg-[#0B1220] text-white px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium hover:bg-[#0B1220]/90 inline-flex items-center gap-2 min-w-0">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : null}
                  <span className="truncate sm:hidden">{isLast ? "Submit" : "Next"}</span>
                  <span className="hidden sm:inline truncate">{nextLabel}</span>
                  <ChevronRight className="h-4 w-4 shrink-0" />
                </button>
              </div>
            </div>

            <p className="mt-8 text-xs text-slate-500 text-center">
              <Link to={"/" as never} className="underline">Back to whatfinland.com</Link>
            </p>

            {/* Mobile sign out */}
            <div className="lg:hidden mt-4 text-center">
              <button onClick={signOut} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                <LogOut className="h-3.5 w-3.5" /> Sign Out
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function Card({ num, title, subtitle, children }: { num: number; title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 mb-4 shadow-sm">
      <header className="flex items-start gap-3 mb-4">
        <span className="h-7 w-7 rounded-full bg-[#0B1220] text-white text-xs font-semibold flex items-center justify-center shrink-0">{num}</span>
        <div>
          <h2 className="text-base font-semibold text-[#0B1220]">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="text-xs font-medium text-slate-600 block mb-1">{children}</label>;
}

function Field({ label, value, onChange, placeholder, type = "text" }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
    </div>
  );
}

function CityPickerField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const cities = usePublicCities();
  const lower = (value || "").trim().toLowerCase();
  const matched =
    cities.find((c) => c.slug.toLowerCase() === lower) ||
    cities.find((c) => c.name.toLowerCase() === lower);
  const selectValue = matched?.name ?? "";
  const legacy = !!value && !matched && cities.length > 0;
  return (
    <div>
      <Label>City / Destination</Label>
      <select
        value={selectValue}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
      >
        <option value="">— Select A City —</option>
        {cities.map((c) => (
          <option key={c.slug} value={c.name}>{c.name}</option>
        ))}
        {legacy && <option value={value}>{value} (unlisted)</option>}
      </select>
      {legacy && (
        <p className="mt-1 text-[11px] text-amber-700">
          "{value}" isn't in the admin city list — pick an available one so your listing can go live.
        </p>
      )}
    </div>
  );
}

function ChannelRow({ name, color, letter }: { name: string; color: string; letter: string }) {
  const [connected, setConnected] = useState(false);
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <div className="flex items-center gap-3">
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center text-sm font-bold ${color}`}>{letter}</span>
        <span className="text-sm font-medium text-slate-800">{name}</span>
      </div>
      <button onClick={() => setConnected((v) => !v)}
        className={`text-xs font-semibold px-3 py-1.5 rounded-full ${connected ? "bg-emerald-100 text-emerald-700" : "text-[#3454FF] hover:bg-[#3454FF]/10"}`}>
        {connected ? "Connected" : "Connect"}
      </button>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 border-b border-slate-100 py-2 last:border-0">
      <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500 shrink-0">{label}</div>
      <div className="text-sm text-slate-900 sm:text-right sm:max-w-[60%] break-words">{value}</div>
    </div>
  );
}
